/**
 * @file src/shell/Label.js
 * Nhãn 3D bằng CSS2D. Nâng từ three-lessons/src/labels/mozaLabel.js.
 *
 * ĐÂY LÀ CÁCH DUY NHẤT để nút "Ẩn/hiện text" của taskbar điều khiển được text.
 * Text vẽ bằng DOM thủ công hoặc Sprite tự tạo sẽ KHÔNG tuân theo nút đó —
 * đây là lỗi phổ biến nhất khi viết tiết mới. Xem AGENT.md mục 4.2.
 */

import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';

/**
 * @typedef {Object} LabelHandle
 * @property {HTMLElement} el
 * @property {CSS2DObject} obj
 * @property {(text: string) => void} setText
 * @property {(v: boolean) => void} setVisible
 * @property {(variant: string, on?: boolean) => void} setVariant
 * @property {() => void} remove
 */

/**
 * @typedef {Object} LabelSystem
 * @property {(o: {key: string, text?: string, follow?: import('three').Object3D,
 *   position?: [number,number,number], offset?: [number,number,number], variant?: string}) => LabelHandle} create
 * @property {(on: boolean) => void} setTextVisible
 * @property {() => boolean} isTextVisible
 * @property {(id: string, h: {show: () => void, hide: () => void}) => () => void} registerGroup
 * @property {() => void} retranslate  Dịch lại text của mọi nhãn tạo bằng `key` (không phải `text` literal)
 * @property {(w: number, h: number) => void} resize
 * @property {() => void} dispose
 */

/**
 * @param {{ stage: HTMLElement, engine: any, t: (k: string, v?: any) => string }} opts
 * @returns {LabelSystem}
 */
export function createLabelSystem(opts) {
  const renderer = new CSS2DRenderer();
  renderer.domElement.className = 'ol-labels';
  opts.stage.appendChild(renderer.domElement);

  /**
   * __key/__usesKey/__apply là phần NỘI BỘ, không nằm trong LabelHandle công khai:
   * retranslate() cần key gốc, setTextVisible() cần apply() để tính lại display
   * theo CẢ hai cờ (cục bộ + toàn cục).
   * @type {Set<LabelHandle & {__key: string, __usesKey: boolean, __apply: () => void}>}
   */
  const labels = new Set();
  /** @type {Map<string, {show: () => void, hide: () => void}>} */
  const groups = new Map();
  let textVisible = true;

  // CSS2DRenderer phải render mỗi frame, sau khi camera đã update
  const offUpdate = opts.engine.onUpdate(() => {
    renderer.render(opts.engine.scene, opts.engine.camera);
  });

  const resize = (/** @type {number} */ w, /** @type {number} */ h) => renderer.setSize(w, h);
  resize(opts.stage.clientWidth || 1, opts.stage.clientHeight || 1);

  return {
    create(o) {
      const el = document.createElement('div');
      el.className = 'ol-label' + (o.variant ? ` ol-label--${o.variant}` : '');
      // wantedVisible: ý muốn CỤC BỘ của tiết (đặt qua setVisible), tách khỏi
      // textVisible (cờ TOÀN CỤC của nút "Ẩn/hiện chữ"). Hiện = cả hai cùng đúng.
      // TRƯỚC ĐÂY setTextVisible() ghi đè display trực tiếp, không biết gì về
      // wantedVisible — nên bấm nút "Ẩn/hiện chữ" lúc một nhãn đang bị tiết ẩn
      // (vd nhãn của tab khác) sẽ ép nó HIỆN NHẦM. Giờ luôn tính lại qua apply().
      let wantedVisible = true;
      const usesKey = o.text === undefined;
      /** @type {CSS2DObject | null} */
      let obj = null;
      /**
       * Ẩn/hiện phải đi qua `obj.visible`, KHÔNG chỉ ghi el.style.display.
       *
       * CSS2DRenderer GHI ĐÈ element.style.display ở MỖI frame: nếu object.visible là
       * true nó đặt lại về '' (và 'none' khi nhãn ra ngoài khung nhìn). Bản trước chỉ ghi
       * style.display nên mọi lệnh ẩn — kể cả nút "Ẩn/hiện chữ" của thanh công cụ — bị
       * xoá ngay frame sau, chỉ "có vẻ đúng" khi engine đang dừng vẽ (tab ẩn).
       * Đặt cả hai: `visible` để renderer tôn trọng, `style.display` để đúng ngay cả
       * trước frame đầu tiên (lúc phần tử còn chưa được renderer gắn vào DOM).
       */
      const apply = () => {
        const on = wantedVisible && textVisible;
        if (obj) obj.visible = on;
        el.style.display = on ? '' : 'none';
      };

      el.textContent = o.text ?? opts.t(o.key);

      obj = new CSS2DObject(el);
      if (o.offset) obj.position.set(...o.offset);
      else if (o.position) obj.position.set(...o.position);

      if (o.follow) o.follow.add(obj);
      else opts.engine.scene.add(obj);

      apply();

      /** @type {LabelHandle & {__key: string, __usesKey: boolean, __apply: () => void}} */
      const handle = {
        __key: o.key,
        __usesKey: usesKey,
        __apply: apply,
        el,
        obj,
        setText: (text) => {
          el.textContent = text;
        },
        setVisible: (v) => {
          wantedVisible = Boolean(v);
          apply();
        },
        setVariant: (variant, on = true) => el.classList.toggle(`ol-label--${variant}`, on),
        remove: () => {
          obj.parent?.remove(obj);
          el.remove();
          labels.delete(handle);
        },
      };
      labels.add(handle);
      return handle;
    },

    setTextVisible(on) {
      textVisible = on;
      for (const l of labels) l.__apply();
      for (const g of groups.values()) (on ? g.show : g.hide)();
    },

    isTextVisible: () => textVisible,

    retranslate() {
      for (const l of labels) if (l.__usesKey) l.el.textContent = opts.t(l.__key);
    },

    /**
     * Lối thoát cho text đặc biệt mà create() không làm được (vd nhóm mesh chữ
     * nằm sẵn trong file .glb). Đừng tự thêm nút ẩn/hiện text riêng của tiết.
     */
    registerGroup(id, h) {
      groups.set(id, h);
      if (!textVisible) h.hide();
      return () => groups.delete(id);
    },

    resize,

    dispose() {
      offUpdate();
      for (const l of [...labels]) l.remove();
      groups.clear();
      renderer.domElement.remove();
    },
  };
}

export { CSS2DObject };
